import { Alert, Badge, Group, Paper, Select, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { PiWarning } from 'react-icons/pi';
import { notifications } from '@mantine/notifications';
import { PortfolioOwnerLink } from '@/components/client-portfolio/PortfolioOwnerLink';
import Link from '@/components/Link';
import {
  apiErrorText,
  CLIENT_COMPANY_STATUS_COLORS,
  CLIENT_COMPANY_STATUS_LABELS,
  jurisdictionTitle,
  useOfferClientCompanyCandidates,
  useSetOfferClientCompany,
} from '@/models/clientCompanies';
import type { TOffer } from '@/models/offers';
import { useServices } from '@/models/services';
import {
  CLIENT_PORTFOLIO_VIEW,
  useHasAnyPermission,
} from '@/models/permissions';

function selectedParentServiceIds(offer: TOffer): number[] {
  const sel = offer.customerSelections;
  if (!sel) return [];
  const ids = new Set<number>();
  for (const p of sel.proposals) {
    for (const b of p.blocks) {
      for (const s of b.services) {
        const picked =
          b.selectedTier != null
            ? !!s.tiers[b.selectedTier]?.included
            : s.selected;
        if (picked) ids.add(s.serviceId);
      }
    }
  }
  for (const s of sel.standaloneServices) if (s.selected) ids.add(s.serviceId);
  return [...ids];
}

export function OfferClientCompanyCard({ offer }: { offer: TOffer }) {
  const { data, isLoading, error } = useOfferClientCompanyCandidates(offer.id);
  const set = useSetOfferClientCompany(offer.id);
  const canOpen = useHasAnyPermission(CLIENT_PORTFOLIO_VIEW);
  const owner = offer.portfolioOwner ?? null;
  const settled =
    owner != null ||
    ['paid', 'fully-paid', 'partially-paid'].includes(
      offer.invoiceStatus ?? '',
    ) ||
    (offer.paidServices ?? 0) > 0;
  const ownerCompany =
    owner?.kind === 'client_company'
      ? data?.companies.find((c) => String(c.id) === String(owner.id))
      : undefined;
  const current =
    data?.clientCompanyId != null ? String(data.clientCompanyId) : null;
  const selected = data?.companies.find((c) => String(c.id) === current);
  const parentIds = useMemo(() => selectedParentServiceIds(offer), [offer]);
  const { data: packageServices } = useServices(
    current && !owner ? parentIds : [],
  );
  const registering = packageServices.filter(
    (s): s is NonNullable<typeof s> => !!s?.registersCompany,
  );

  const change = (v: string | null) =>
    set.mutateAsync(v ? Number(v) : null).catch((e) =>
      notifications.show({
        color: 'red',
        title: 'Client company not changed',
        message: apiErrorText(e),
      }),
    );

  return (
    <Paper withBorder p="sm">
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <Text fw={500} fz="sm">
            Client company
          </Text>
          {selected && (
            <Badge
              size="xs"
              variant="light"
              color={CLIENT_COMPANY_STATUS_COLORS[selected.status]}
            >
              {CLIENT_COMPANY_STATUS_LABELS[selected.status]}
            </Badge>
          )}
        </Group>
        <Select
          size="xs"
          placeholder={
            isLoading
              ? 'Loading...'
              : error
                ? 'Company choice is not available to you'
                : (data?.companies.length ?? 0) === 0
                  ? 'The account has no client companies yet'
                  : 'New sale - no existing company'
          }
          data={(data?.companies ?? []).map((c) => ({
            value: String(c.id),
            label: `${c.name}${c.registrationNumber ? ` - ${c.registrationNumber}` : ''}${c.jurisdiction ? `, ${jurisdictionTitle(c.jurisdiction)}` : ''}`,
          }))}
          value={current}
          onChange={change}
          clearable
          searchable
          disabled={
            settled ||
            !!error ||
            set.isPending ||
            (data?.companies.length ?? 0) === 0
          }
        />
        {current && !owner && registering.length > 0 && (
          <Alert color="yellow" variant="light" p="xs" icon={<PiWarning />}>
            <Text fz="xs">
              The package registers a company (
              {registering.map((s) => s.name.trim()).join(', ')}). With an
              existing company selected, Settle adds the services to its
              portfolio and does not create a new company.
            </Text>
          </Alert>
        )}
        {owner ? (
          <PortfolioOwnerLink
            owner={owner}
            label={
              owner.kind === 'client_company'
                ? `Portfolio: ${ownerCompany?.name ?? `company #${owner.id}`}`
                : `Portfolio: ${offer.contactName} - services without a company`
            }
          />
        ) : (
          selected &&
          canOpen && (
            <Link to={`~/client-companies/${selected.id}`}>
              Open company portfolio
            </Link>
          )
        )}
        <Text fz="xs" c="dimmed">
          {owner
            ? 'Settled: the services of this offer are in this portfolio. Delivery is marked there.'
            : settled
              ? 'Settled, the portfolio is not created yet.'
              : current
                ? 'Upsell: after Settle the services go to this company portfolio.'
                : 'The portfolio is created after Settle: a new company in registration if the package registers a company, otherwise the contact portfolio.'}
        </Text>
      </Stack>
    </Paper>
  );
}
